import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { getQuizQuestions } from '../utils/firestoreApi';
import VideoPlayerIframe from './VideoPlayerIframe';

const QuizContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 500px;
  color: white;
  box-sizing: border-box;
  height: 100%;
`;

const QuizLayout = styled(motion.div)`
  display: grid;
  grid-template-rows: 8vh 64vh;
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
  margin: 2vh 0;
`;

const TitleArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VideoArea = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  max-width: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    height: 100%;
  }
`;

const VideoContainer = styled.div`
  width: 100%;
  height: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 9/16;
  border-radius: 25px;

  @media (max-width: 768px) {
    max-width: 350px;
  }
`;

const QuizCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 24px;
  backdrop-filter: blur(10px);
  box-shadow: rgba(0, 0, 0, 0.3) 0px 8px 32px;
  width: 80%;
  max-width: 320px;
  min-width: 320px;
  margin: 0 auto;
  gap: 24px;

  @media (max-width: 768px) {
    padding: 16px;
    max-width: 300px;
  }
`;

const QuestionText = styled(motion.h2)`
  text-align: center;
  font-size: calc(1.8vh + 0.8rem);
  margin-bottom: 16px;
  color: #fff;
  font-weight: normal;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const ChoicesContainer = styled.div`
  width: 100%;
`;

const ChoiceItem = styled(motion.div)`
  margin: 2px 0;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateX(8px);
  }
`;

const RadioButton = styled.input`
  color: rgba(255, 255, 255, 0.3);
  
  &:checked {
    color: #FFB6C1;
  }
  
  padding: 6px;
`;

const ChoiceLabel = styled(motion.label)`
  color: ${props => props.selected ? '#ff3369' : '#fff'};
  transition: color 0.2s ease;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 10px;
  background: ${props => props.selected ? 'rgba(255, 51, 105, 0.1)' : 'transparent'};
  border: ${props => props.selected ? '2px solid rgba(255, 51, 105, 0.3)' : '2px solid transparent'};

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 5px 8px;
  }
`;

const InfoText = styled.div`
  color: rgb(255, 182, 193);
  font-weight: 600;
  font-size: 0.85rem;
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.3s ease;
  font-weight: 300;
  white-space: nowrap;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const SubmitButton = styled(motion.button)`
  background: ${props => props.selected ? '#fff' : 'transparent'};
  border-radius: 15px;
  padding: 8px 30px;
  height: 45px;
  font-size: 1.4rem;
  width: 60%;
  max-width: 200px;
  color: ${props => props.selected ? '#000' : 'rgba(136, 136, 136, 0.5)'};
  text-transform: none;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  border: ${props => props.selected ? '2px solid #ffffff' : '2px solid rgba(136, 136, 136, 0.5)'};
  cursor: ${props => props.selected ? 'pointer' : 'default'};
  display: flex;
  align-items: center;
  justify-content: center;
    
  @media (max-width: 768px) {
    height: 45px;
    padding: 8px 20px;
    font-size: 1.2rem;
    width: 80%;
  }

  &:hover {
    background: ${props => props.selected ? '#ffffff' : 'transparent'};
    color: ${props => props.selected ? '#000' : 'rgba(136, 136, 136, 0.5)'};
    border: ${props => props.selected ? '2px solid #ffffff' : '2px solid rgba(136, 136, 136, 0.5)'};
  }
  
  &:disabled {
    border: 2px solid rgba(136, 136, 136, 0.5);
    color: rgba(136, 136, 136, 0.5);
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 50px;
  
  @media (max-width: 768px) {
    gap: 5px;
    margin-top: 0px;
    margin-bottom: 5px;
  }
`;

const ProblemTitle = styled.h1`
  color: #fff;
  font-size: 2rem;
  font-weight: 400;
  position: relative;
  text-align: center;
  margin-bottom: 30px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 3px;
    background-color: #fff;
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    margin-top: 0px;
    font-size: 1.5rem;
  }
`;

const LoadingText = styled.div`
  color: #fff;
  font-size: 1.2rem;
  text-align: center;
  margin: 20px 0;
`;

const ErrorText = styled.div`
  color: #ff6b6b;
  font-size: 1.1rem;
  text-align: center;
  margin: 20px 0;
  padding: 20px;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 10px;
`;

function QuizGame({ onComplete, isFirst, isSecond, isThird }) {
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [isVideoComplete, setIsVideoComplete] = useState(false);
  const [quizData, setQuizData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 퀴즈 데이터 로드
  useEffect(() => {
    const loadQuizData = async () => {
      try {
        setLoading(true);
        const data = await getQuizQuestions();
        if (data) {
          setQuizData(data);
        } else {
          setError('퀴즈 데이터를 찾을 수 없습니다.');
        }
      } catch (err) {
        //console.error('퀴즈 데이터 로드 실패:', err);
        setError('퀴즈 데이터를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    loadQuizData();
  }, []);
  
  // 현재 문제 인덱스 결정
  const getCurrentQuestionIndex = () => {
    if (isFirst) return 0;
    if (isSecond) return 1;
    if (isThird) return 2;
    return 0; // 기본값
  };
  
  const currentQuestionIndex = getCurrentQuestionIndex();
  const currentQuestion = quizData[currentQuestionIndex];

  // 모든 문제에서 영상 끝나면 문제 표시
  useEffect(() => {
    setIsVideoComplete(false); // 문제 시작할 때 초기화
    setSelectedAnswer(''); // 선택된 답변도 초기화
  }, [isFirst, isSecond, isThird]);

  const handleSubmit = () => {
    if (selectedAnswer) {
      onComplete && onComplete(selectedAnswer);
    }
  };

  // 로딩 중이거나 에러가 있으면 처리
  // if (loading) {
  //   return (
  //     <QuizContainer>
  //       <LoadingText>퀴즈 문제를 불러오는 중...</LoadingText>
  //     </QuizContainer>
  //   );
  // }

  if (error) {
    return (
      <QuizContainer>
        <ErrorText>{error}</ErrorText>
      </QuizContainer>
    );
  }

  // 퀴즈 데이터가 없거나 현재 문제가 없으면 처리
  if (!quizData.length || !currentQuestion) {
    return (
      <QuizContainer>
        <ErrorText>퀴즈 문제를 찾을 수 없습니다.</ErrorText>
      </QuizContainer>
    );
  }

  return (
    <QuizContainer>
      <AnimatePresence mode="wait">
        {!isVideoComplete ? (
          <QuizLayout
            key="video"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <TitleArea>
              <ProblemTitle>{currentQuestionIndex + 1}번 문제</ProblemTitle>
            </TitleArea>
            <VideoArea>
              <VideoWrapper>
                <VideoContainer>
                  <VideoPlayerIframe 
                    videoUrl={`https://www.youtube.com/watch?v=${currentQuestion.videoId}`}
                    onVideoEnd={() => setIsVideoComplete(true)}
                    containerType="quiz"
                    showControls={false}
                  />
                </VideoContainer>
              </VideoWrapper>
            </VideoArea>
          </QuizLayout>
        ) : (
          <QuizCard
            key="quiz"
            initial={{ 
              opacity: 0, 
              y: 50, 
              scale: 0.9,
              rotateX: -10
            }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              rotateX: 0
            }}
            transition={{ 
              duration: 0.8,
              type: "spring",
              stiffness: 100,
              damping: 15
            }}
          >
            <QuestionText
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.6,
                delay: 0.3,
                ease: "easeOut"
              }}
            >
              {currentQuestion.question.text}
            </QuestionText>
            
            <ChoicesContainer>
              {currentQuestion.question.options.map((option, index) => (
                <ChoiceItem 
                  key={index}
                  layout
                  initial={{ opacity: 0, x: -30, scale: 0.9 }}
                  animate={{ 
                    opacity: 1, 
                    x: 0, 
                    scale: selectedAnswer === option ? 1.05 : 1
                  }}
                  transition={{ 
                    duration: 0.4,
                    delay: 0.5 + (index * 0.1),
                    ease: "easeOut",
                    layout: { duration: 0.3, ease: "easeInOut" },
                    scale: { duration: 0.2, ease: "easeInOut" }
                  }}
                  whileHover={{ 
                    x: 8,
                    scale: selectedAnswer === option ? 1.08 : 1.02,
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ 
                    scale: 0.99,
                    transition: { duration: 0.05 }
                  }}
                >
                  <ChoiceLabel 
                    selected={selectedAnswer === option}
                    layout
                    animate={{
                      scale: selectedAnswer === option ? 1.02 : 1
                    }}
                    transition={{ 
                      duration: 0.2,
                      layout: { duration: 0.3, ease: "easeInOut" }
                    }}
                    whileHover={{
                      scale: selectedAnswer === option ? 1.05 : 1.02,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <RadioButton
                      type="radio"
                      name="quiz-choice"
                      value={option}
                      checked={selectedAnswer === option}
                      onChange={(e) => setSelectedAnswer(e.target.value)}
                    />
                    {option}
                  </ChoiceLabel>
                </ChoiceItem>
              ))}
            </ChoicesContainer>
            
            <ButtonContainer>
              <InfoText show={selectedAnswer}>
                {isThird ? '* 원하는 경품에 옹모해보세요!' : '* 다음 문제를 풀고 당첨 확률을 높여보세요!'}
              </InfoText>
                          <SubmitButton
              onClick={handleSubmit}
              disabled={!selectedAnswer}
              selected={!!selectedAnswer}
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ 
                opacity: 1, 
                y: 0, 
                scale: 1 
              }}
              transition={{ 
                duration: 0.5,
                delay: 0.8,
                ease: "easeOut"
              }}
              whileHover={{
                scale: selectedAnswer ? 1.05 : 1,
                y: selectedAnswer ? -3 : 0,
                transition: { duration: 0.2 }
              }}
              whileTap={{
                scale: selectedAnswer ? 0.95 : 1,
                transition: { duration: 0.1 }
              }}
            >
              {isThird ? '응모하기' : '다음 문제'}
            </SubmitButton>
            </ButtonContainer>
          </QuizCard>
        )}
      </AnimatePresence>
    </QuizContainer>
  );
}

export default QuizGame; 